import { FlagOutlined } from "@ant-design/icons"
import { Alert, Empty, Flex, Table, type TableProps, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { TABLE_SURFACES } from "../../../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../../../components/Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../../hooks/use-column-resize.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useTableColumnConfig } from "../../../../hooks/use-table-column-config.tsx"
import { budgetFormat } from "../../../../utils/number/budget-format.ts"
import { type LeadsFilterOptions } from "../../../Leads/redux/leads.types.ts"
import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"
import { type Lead } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { OverviewCardTitle } from "../overview/OverviewCardIcon.tsx"
import { LeadScopeMilestoneFinancialSummary } from "./LeadScopeMilestoneFinancialSummary.tsx"
import { LeadScopeMilestoneProjectTypeTabs } from "./LeadScopeMilestoneProjectTypeTabs.tsx"
import { LeadScopeMilestoneTimelineAlert } from "./LeadScopeMilestoneTimelineAlert.tsx"
import { LeadScopeMilestonesEditor } from "./LeadScopeMilestonesEditor.tsx"
import {
  MILESTONE_TABLE_WIDTH,
  MILESTONE_TABLE_COLUMN_WIDTHS as widths
} from "./lead-scope-milestone-layout.ts"
import {
  type PlanningWindow,
  milestoneEndsBeforeStart,
  milestonesHaveSaveableDates,
  milestonesTimelineWarnings
} from "./lead-scope-milestone-timeline.ts"
import {
  isMilestoneNamed,
  milestoneDuration,
  milestonePayableExceedsReceivable,
  milestonePrice,
  milestonesAreNamed,
  milestonesDurationTotal,
  milestonesSaveRefusal,
  milestonesTotal,
  serializeMilestonesForPatch,
  sortMilestones
} from "./lead-scope-milestones.ts"

const { Text } = Typography

interface ScopeMilestonesValues {
  project_phases: LeadFormDataPhase[]
}

interface LeadScopeMilestonesCardProps {
  lead: Lead
  filterOptions?: LeadsFilterOptions
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadScopeMilestonesCard({
  lead,
  filterOptions,
  canEdit,
  onPatch
}: LeadScopeMilestonesCardProps) {
  const { t } = useTranslation()
  const { userFormat, normalizeDate } = useDateFormat()

  const milestones = useMemo(
    () => sortMilestones(lead?.project_phases as LeadFormDataPhase[] | null),
    [lead?.project_phases]
  )

  const projectTypes = useMemo(() => {
    const configuredNames = new Map(
      (filterOptions?.project_type ?? []).map((type) => [
        String(type.value),
        String(type.label)
      ])
    )
    const detailedNames = new Map(
      (lead?.project_type_details ?? []).map((type) => [type.id, type.name])
    )

    return (lead?.project_type ?? []).map((id) => ({
      id,
      name: configuredNames.get(id) ?? detailedNames.get(id) ?? id
    }))
  }, [
    filterOptions?.project_type,
    lead?.project_type,
    lead?.project_type_details
  ])
  const [activeProjectTypeId, setActiveProjectTypeId] = useState(
    projectTypes[0]?.id ?? ""
  )
  const showProjectTypeTabs = projectTypes.length > 1

  useEffect(() => {
    if (!projectTypes.some(({ id }) => id === activeProjectTypeId)) {
      setActiveProjectTypeId(projectTypes[0]?.id ?? "")
    }
  }, [activeProjectTypeId, projectTypes])

  const visibleMilestones = useMemo(
    () =>
      showProjectTypeTabs
        ? milestones.filter(
            (milestone) => milestone.project_type === activeProjectTypeId
          )
        : milestones,
    [activeProjectTypeId, milestones, showProjectTypeTabs]
  )

  const projectTypeTabs = (onChange = setActiveProjectTypeId) => (
    <LeadScopeMilestoneProjectTypeTabs
      projectTypes={projectTypes}
      activeProjectTypeId={activeProjectTypeId}
      onChange={onChange}
    />
  )

  const showDate = (value?: string | null) =>
    normalizeDate(value, userFormat ?? undefined) ?? "—"

  const showDuration = (value: number) =>
    value
      ? t("leadWorkspace.scope.milestones.durationDays", { count: value })
      : "—"

  const financialMismatch = milestones.some(milestonePayableExceedsReceivable)
  const totalPrice = milestonesTotal(milestones)
  const planning: PlanningWindow = {
    start_date: lead?.start_date,
    end_date: lead?.end_date
  }
  const timelineWarnings = milestonesTimelineWarnings(
    milestones,
    planning,
    (value) => normalizeDate(value)
  )

  const fullColumns: TableProps<LeadFormDataPhase>["columns"] = [
    {
      key: "name",
      width: widths.name,
      title: t("leadWorkspace.scope.milestones.columns.name"),
      render: (_, row) => (
        <Text>
          {row?.name?.trim() || t("leadWorkspace.scope.milestones.unnamed")}
        </Text>
      )
    },
    {
      key: "start",
      width: widths.date,
      title: t("leadWorkspace.scope.milestones.columns.start"),
      render: (_, row) => showDate(row?.start_date)
    },
    {
      key: "end",
      width: widths.date,
      title: t("leadWorkspace.scope.milestones.columns.end"),
      render: (_, row) => showDate(row?.end_date)
    },
    {
      key: "duration",
      width: widths.duration,
      title: t("leadWorkspace.scope.milestones.columns.duration"),
      render: (_, row) => showDuration(milestoneDuration(row))
    },
    {
      key: "price",
      width: widths.money,
      align: "right",
      title: t("leadWorkspace.scope.milestones.columns.price"),
      render: (_, row) => budgetFormat(milestonePrice(row))
    }
  ]

  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.leadScopeMilestones,
    "default",
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<LeadFormDataPhase>({
    fullConfig,
    setColumns
  })

  return (
    <Flex vertical gap={"middle"}>
      <LeadScopeMilestoneFinancialSummary
        salesPrice={lead?.sales_price}
        totalMilestones={totalPrice}
        hasMilestones={milestones.length > 0}
        onReviewMilestones={() => {
          document
            .getElementById("scope-milestones")
            ?.scrollIntoView?.({ behavior: "smooth", block: "start" })
        }}
      />
      <div id={"scope-milestones"}>
        <WorkspaceCard<ScopeMilestonesValues>
          cardId={"scope-milestones"}
          title={
            <OverviewCardTitle icon={<FlagOutlined />} accent={"indigo"}>
              {t("leadWorkspace.scope.milestones.title")}
            </OverviewCardTitle>
          }
          ariaTitle={t("leadWorkspace.scope.milestones.title")}
          canEdit={canEdit}
          values={{ project_phases: milestones }}
          validate={({ project_phases }) =>
            milestonesAreNamed(project_phases) &&
            milestonesHaveSaveableDates(project_phases)
          }
          onSave={async ({ project_phases = [] }) => {
            const refusal: { message: string | null } = { message: null }
            const saved = await onPatch(
              { project_phases: serializeMilestonesForPatch(project_phases) },
              {
                onRejected: (payload) => {
                  refusal.message = milestonesSaveRefusal(
                    payload,
                    project_phases,
                    (milestone) =>
                      milestone.name?.trim() ||
                      t("leadWorkspace.scope.milestones.unnamed")
                  )
                }
              }
            )
            if (!saved && refusal.message) throw new Error(refusal.message)
            return saved
          }}
          renderEdit={({ values, setValues, saving }) => (
            <>
              {projectTypeTabs((nextProjectTypeId) => {
                const unsaveable = values.project_phases.find(
                  (milestone) =>
                    !isMilestoneNamed(milestone) ||
                    milestoneEndsBeforeStart(milestone)
                )
                setActiveProjectTypeId(
                  unsaveable?.project_type || nextProjectTypeId
                )
              })}
              <LeadScopeMilestonesEditor
                milestones={values.project_phases}
                activeProjectTypeId={activeProjectTypeId}
                filterByProjectType={showProjectTypeTabs}
                defaultProjectTypeId={
                  showProjectTypeTabs
                    ? activeProjectTypeId
                    : lead?.project_type?.[0]
                }
                salesPrice={lead?.sales_price}
                planning={planning}
                saving={saving}
                onChange={(project_phases) => setValues({ project_phases })}
              />
            </>
          )}
        >
          <>
            {financialMismatch && (
              <Alert
                showIcon
                type={"warning"}
                data-testid={"milestones-financial-alert"}
                message={t(
                  "leadWorkspace.scope.milestones.payableExceedsReceivable"
                )}
                className={"mb-3"}
              />
            )}
            <LeadScopeMilestoneTimelineAlert
              warnings={timelineWarnings}
              planning={planning}
              className={"mb-3"}
            />
            {projectTypeTabs()}
            {/* The header row stays visible even with zero rows — a table
                that shows its own structure reads as "nothing here yet",
                not as a broken or unfinished page (2026-08-30). */}
            <div
              className={"w-full"}
              data-testid={"milestones-table-container"}
              ref={tableWrapRef}
            >
              <Table<LeadFormDataPhase>
                size={"small"}
                rowKey={"id"}
                pagination={false}
                scroll={{ x: MILESTONE_TABLE_WIDTH }}
                tableLayout={"fixed"}
                columns={decorateColumns(columns)}
                components={{ header: { cell: ResizableHeaderCell } }}
                dataSource={visibleMilestones}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      imageStyle={{ height: 32 }}
                      className={"!my-3"}
                      description={
                        <Flex vertical gap={2} align={"center"}>
                          <Text>
                            {t("leadWorkspace.scope.milestones.empty")}
                          </Text>
                          <Text type={"secondary"} className={"text-xs"}>
                            {t(
                              "leadWorkspace.scope.milestones.emptyDescription"
                            )}
                          </Text>
                        </Flex>
                      }
                    />
                  )
                }}
                summary={
                  visibleMilestones.length
                    ? () => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <Text strong>
                              {t("leadWorkspace.scope.milestones.total")}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3}>
                            <Text
                              strong
                              data-testid={"milestones-duration-total"}
                            >
                              {showDuration(
                                milestonesDurationTotal(visibleMilestones)
                              )}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} align={"right"}>
                            <Text strong data-testid={"milestones-total"}>
                              {budgetFormat(milestonesTotal(visibleMilestones))}
                            </Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )
                    : undefined
                }
              />
            </div>
          </>
        </WorkspaceCard>
      </div>
    </Flex>
  )
}

export { type ScopeMilestonesValues, LeadScopeMilestonesCard }
